import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ActivityAsyncAdapter } from './activity.adapter';
import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { IMicrosoftTeamsActivity, MicrosoftTeamsActivity, MicrosoftTeamsLinkedService, ModelConstants, PostInType, ReferenceAdapter } from 'shell-models-lib';
import { ValueAdapter } from '../value.adapter';
import { IMicrosoftTeamsActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { getFabricLsForParameterizedConnections } from '../utils';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(MicrosoftTeamsActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            chatId: '*',
            operationType: '*',
            content: '*',
            groupId: '*',
            channelId: '*',
            subject: '*'
        },
        externalReferences: '*'
    }
})
export class MicrosoftTeamsActivityAdapter extends ActivityAsyncAdapter<MicrosoftTeamsActivity, IMicrosoftTeamsActivity, IMicrosoftTeamsActivityContext> {
    protected async _toResource(activity: MicrosoftTeamsActivity): Promise<IMicrosoftTeamsActivity> {
        const modelmicrosoftTeamsActivity = super._getCommonActivityProperties(
            activity, Metamodel.modelFor(MicrosoftTeamsActivity).name) as IMicrosoftTeamsActivity;
        modelmicrosoftTeamsActivity.typeProperties = {
            operationType: activity.operationType === PostInType.SendMessageInChannel ? 'SendMessageInChannel' : 'SendMessageInGroupChat',
            content: MicrosoftTeamsActivity.CONTENT.templateValue(activity),
            subject: ValueAdapter.toResource(activity, MicrosoftTeamsActivity.SUBJECT)
        };
       
        switch (activity.operationType) {
            case PostInType.SendMessageInChannel:
                modelmicrosoftTeamsActivity.typeProperties.teamId = ValueAdapter.toResource(activity, MicrosoftTeamsActivity.TEAMID);
                modelmicrosoftTeamsActivity.typeProperties.channelId = ValueAdapter.toResource(activity, MicrosoftTeamsActivity.CHANNELID);
                break;
            case PostInType.SendMessageInGroupChat:
                modelmicrosoftTeamsActivity.typeProperties.chatId = ValueAdapter.toResource(activity, MicrosoftTeamsActivity.CHATID);
                break;
            default:
        }

        modelmicrosoftTeamsActivity.linkedServiceName = this._toLinkedServiceReferenceModel(activity.linkedService,
            activity.linkedServiceParameters);

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelmicrosoftTeamsActivity, parsed);
        }

        return modelmicrosoftTeamsActivity;
    }

    protected async _toModel(modelActivity: IMicrosoftTeamsActivity): Promise<MicrosoftTeamsActivity> {
        const activity: MicrosoftTeamsActivity = MicrosoftTeamsActivity.MODEL.create<MicrosoftTeamsActivity>(modelActivity.name, modelActivity.description);
        this._updatedCommonProperties(modelActivity, activity);

        if (modelActivity.typeProperties.operationType === 'SendMessageInChannel') {
            activity.operationType = PostInType.SendMessageInChannel;
        } else if (modelActivity.typeProperties.operationType === 'SendMessageInGroupChat') {
            activity.operationType = PostInType.SendMessageInGroupChat;
        }

        MicrosoftTeamsActivity.CONTENT.setTemplateValue(activity, modelActivity.typeProperties.content);
        ValueAdapter.toModel(activity, MicrosoftTeamsActivity.CHATID, modelActivity.typeProperties.chatId);
        ValueAdapter.toModel(activity, MicrosoftTeamsActivity.CHANNELID, modelActivity.typeProperties.channelId);
        ValueAdapter.toModel(activity, MicrosoftTeamsActivity.TEAMID, modelActivity.typeProperties.teamId);
        ValueAdapter.toModel(activity, MicrosoftTeamsActivity.SUBJECT, modelActivity.typeProperties.subject);

        if(modelActivity.linkedServiceName !== undefined){
            activity.linkedService = getFabricLsForParameterizedConnections(
                            modelActivity.linkedServiceName,
                            ModelConstants.microsoftTeamsLinkedService, 
                            activity);
            if(activity.linkedService === undefined) {                       
                activity.linkedService = ReferenceAdapter.toProxyModel<MicrosoftTeamsLinkedService>(Metamodel.modelFor(MicrosoftTeamsLinkedService),
                    modelActivity.linkedServiceName,
                    this.context);
            }
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, MicrosoftTeamsActivity.MODEL));
        return activity;
    }
}
